import { useState } from "react";
import { Activity, CalendarEvent, SportType, PlannerProfile, PreferredDays, CommuteConfig, FixedSession, BlockedRange, SPORT_CATEGORY_LABELS } from "../types/training";
import {
  ALL_SPORTS, SPORT_LABELS, sportCategory, detectSportsFromHistory, emptyProfile, DEFAULT_PREFERRED_DAYS, recentVolume,
} from "../COMPONENTS/plannerProfile";
import "../CSS/onboarding.scss";
import { usePageBackground } from "../COMPONENTS/usePageBackground";

const DAY_LABELS = ["Mån", "Tis", "Ons", "Tor", "Fre", "Lör", "Sön"];
const MONTHS = ["jan", "feb", "mar", "apr", "maj", "jun", "jul", "aug", "sep", "okt", "nov", "dec"];

const RACE_CAT = /^RACE(_[A-D])?$/i;
function isRace(e: CalendarEvent) { return !!e.category && RACE_CAT.test(e.category); }

// Suggest a sensible TIME ceiling: the athlete's typical recent weekly volume
// (last 6 weeks) plus a little headroom. This is "how much time you have most
// weeks" — NOT your biggest-ever weeks (those overshoot) nor an acute injury
// ceiling. The plan then uses only as much of this as the race actually needs,
// so the suggestion and the planned peak are intentionally different numbers.
function suggestMaxHours(activities: Activity[]): number | null {
  const { weeklyHours } = recentVolume(activities, "Run");
  if (weeklyHours <= 0) return null;
  return Math.round(weeklyHours * 1.15 * 2) / 2; // typical + ~15% headroom, nearest 0.5h
}

interface Props {
  activities: Activity[];
  events: CalendarEvent[];
  initialProfile?: PlannerProfile | null;
  onComplete: (profile: PlannerProfile) => void;
}

export function OnboardingWizard({ activities, events, initialProfile, onComplete }: Props) {
  usePageBackground();
  const detected = detectSportsFromHistory(activities, events);
  const suggestedMax = suggestMaxHours(activities);
  const today = new Date().toISOString().slice(0, 10);
  const calendarRaces = events
    .filter(isRace)
    .filter(e => e.start_date_local.slice(0, 10) > today)
    .sort((a, b) => a.start_date_local.localeCompare(b.start_date_local));

  const [selectedSports, setSelectedSports] = useState<Set<SportType>>(
    initialProfile
      ? new Set(initialProfile.sportsEnjoyed as SportType[])
      : new Set(detected.filter(d => d.count >= 3).map(d => d.sport))
  );
  const [maxWeeklyHours, setMaxWeeklyHours] = useState(initialProfile?.maxWeeklyHours ?? 8);
  const [preferredDays, setPreferredDays] = useState<PreferredDays>(
    initialProfile?.preferredDays ?? { ...DEFAULT_PREFERRED_DAYS }
  );
  const [commute, setCommute] = useState<CommuteConfig | null>(initialProfile?.commute ?? null);
  const [goals, setGoals] = useState(initialProfile?.goals ?? "");
  const [planningMode, setPlanningMode] = useState<"tss" | "duration">(initialProfile?.planningMode ?? "tss");
  const [allowDoubles, setAllowDoubles] = useState<boolean>(initialProfile?.allowDoubles ?? false);
  const [doubleThreshold, setDoubleThreshold] = useState<boolean>(initialProfile?.doubleThreshold ?? false);
  const [primarySport, setPrimarySport] = useState<SportType | undefined>(initialProfile?.primarySport);
  const [snowStartMonth, setSnowStartMonth] = useState<number>(initialProfile?.snowStartMonth ?? 11);
  const [snowEndMonth, setSnowEndMonth] = useState<number>(initialProfile?.snowEndMonth ?? 4);
  const [fixedSessions, setFixedSessions] = useState<FixedSession[]>(initialProfile?.fixedSessions ?? []);
  const [blockedRanges, setBlockedRanges] = useState<BlockedRange[]>(initialProfile?.blockedRanges ?? []);
  const [addingSport, setAddingSport] = useState(false);
  const [sportQuery, setSportQuery] = useState("");

  function toggleSport(sport: SportType) {
    setSelectedSports(prev => {
      const next = new Set(prev);
      next.has(sport) ? next.delete(sport) : next.add(sport);
      return next;
    });
  }

  function toggleDay(type: keyof PreferredDays, day: number) {
    setPreferredDays(p => {
      const cur = p[type];
      const next = cur.includes(day) ? cur.filter(d => d !== day) : [...cur, day];
      return { ...p, [type]: next };
    });
  }

  function addFixed() {
    setFixedSessions(fs => [...fs, {
      id: Math.random().toString(36).slice(2, 9),
      day: 2, sport: "Run", durationMin: 45, kind: "easy", biweekly: false, label: "",
    }]);
  }
  function updateFixed(id: string, patch: Partial<FixedSession>) {
    setFixedSessions(fs => fs.map(s => s.id === id ? { ...s, ...patch } : s));
  }
  function removeFixed(id: string) {
    setFixedSessions(fs => fs.filter(s => s.id !== id));
  }

  function addBlocked() {
    const today = new Date().toISOString().slice(0, 10);
    setBlockedRanges(b => [...b, { id: Math.random().toString(36).slice(2, 9), start: today, end: today, label: "" }]);
  }
  function updateBlocked(id: string, patch: Partial<BlockedRange>) {
    setBlockedRanges(b => b.map(r => r.id === id ? { ...r, ...patch } : r));
  }
  function removeBlocked(id: string) {
    setBlockedRanges(b => b.filter(r => r.id !== id));
  }

  function handleSave() {
    const profile: PlannerProfile = {
      ...emptyProfile(),
      sportsEnjoyed: Array.from(selectedSports),
      workoutLibrary: {},
      raceGoals: calendarRaces.map(e => ({
        raceName: e.name,
        raceDate: e.start_date_local.slice(0, 10),
        goalTime: "",
        eventId: e.id,
      })),
      goals,
      preferredDays,
      maxWeeklyHours,
      planningMode,
      allowDoubles,
      doubleThreshold,
      primarySport: primarySport && selectedSports.has(primarySport) ? primarySport : undefined,
      snowStartMonth,
      snowEndMonth,
      fixedSessions,
      blockedRanges,
      commute: commute ?? undefined,
      setupComplete: true,
    };
    onComplete(profile);
  }

  const canSave = selectedSports.size > 0;

  return (
    <div className="ob">
      <div className="ob__page">
        <h1 className="ob__title">Sätt upp din träningsprofil</h1>

        {/* Sports — your sports as bubbles; "+" opens a searchable list to add more */}
        <section className="ob__section">
          <div className="ob__section-label">Sporter</div>
          <div className="ob__chips">
            {Array.from(selectedSports).map(sport => (
              <button
                key={sport}
                className="ob__chip ob__chip--active"
                onClick={() => toggleSport(sport)}
                title="Ta bort"
              >
                {SPORT_LABELS[sport]} <span className="ob__chip-x">×</span>
              </button>
            ))}
            <div className="ob__add">
              <button
                className="ob__chip ob__chip--add"
                onClick={() => { setAddingSport(v => !v); setSportQuery(""); }}
                aria-expanded={addingSport}
                title="Lägg till sport"
              >+</button>
              {addingSport && (() => {
                const q = sportQuery.trim().toLowerCase();
                const opts = ALL_SPORTS.filter(s => !selectedSports.has(s) && SPORT_LABELS[s].toLowerCase().includes(q));
                return (
                  <div className="ob__sport-dropdown">
                    <input
                      autoFocus
                      className="ob__sport-search"
                      placeholder="Sök sport…"
                      value={sportQuery}
                      onChange={e => setSportQuery(e.target.value)}
                    />
                    <div className="ob__sport-list">
                      {(["endurance", "explosive"] as const).map(cat => {
                        const group = opts.filter(s => sportCategory(s) === cat);
                        if (group.length === 0) return null;
                        return (
                          <div key={cat}>
                            <div className="ob__sport-group">{SPORT_CATEGORY_LABELS[cat]}</div>
                            {group.map(s => (
                              <button
                                key={s}
                                className="ob__sport-option"
                                onClick={() => { toggleSport(s); setSportQuery(""); setAddingSport(false); }}
                              >{SPORT_LABELS[s]}</button>
                            ))}
                          </div>
                        );
                      })}
                      {opts.length === 0 && <div className="ob__sport-empty">Ingen träff</div>}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </section>

        {/* Primary sport */}
        {selectedSports.size > 1 && (
          <section className="ob__section">
            <div className="ob__section-label">Huvudsport</div>
            <div className="ob__chips">
              {Array.from(selectedSports).map(sport => (
                <button
                  key={sport}
                  className={`ob__chip${primarySport === sport ? " ob__chip--active" : ""}`}
                  onClick={() => setPrimarySport(sport)}
                >
                  {SPORT_LABELS[sport]}
                </button>
              ))}
            </div>
            <div className="ob__section-hint">
              Din viktigaste sport. Hålls igång med lätta pass även när du tränar
              mot ett lopp i en annan gren, så att grundformen inte tappas.
            </div>
          </section>
        )}

        {/* Snow window — only relevant for skiers */}
        {(selectedSports.has("NordicSki") || selectedSports.has("RollerSki")) && (
          <section className="ob__section">
            <div className="ob__section-label">Snösäsong</div>
            <div className="ob__chips">
              <label className="ob__month-field">
                Snö från
                <select value={snowStartMonth} onChange={e => setSnowStartMonth(parseInt(e.target.value))}>
                  {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
              </label>
              <label className="ob__month-field">
                till
                <select value={snowEndMonth} onChange={e => setSnowEndMonth(parseInt(e.target.value))}>
                  {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
              </label>
            </div>
            <div className="ob__section-hint">
              Längdskidpass blir rullskidor utanför snösäsongen och riktig skidåkning
              under den.
            </div>
          </section>
        )}

        {/* Max hours */}
        <section className="ob__section">
          <div className="ob__section-label">Max timmar per vecka</div>
          <div className="ob__hours-control">
            <input
              className="ob__hours-input"
              type="number" min={2} max={25} step={0.5}
              value={maxWeeklyHours}
              onChange={e => setMaxWeeklyHours(parseFloat(e.target.value))}
            />
            <span className="ob__hours-value">h</span>
            {suggestedMax != null && (
              <button
                className="ob__suggestion"
                onClick={() => setMaxWeeklyHours(suggestedMax)}
              >
                Förslag: {suggestedMax}h
              </button>
            )}
          </div>
          {suggestedMax != null && (
            <div className="ob__section-hint">
              Detta är ett tak (max tid du har de flesta veckor), inte målet.
              Planen använder bara så mycket som ditt lopp kräver, så denna siffra
              och planens toppvecka är med flit olika. Förslaget = din typiska
              veckovolym senaste 6 veckorna + lite marginal.
            </div>
          )}
          <div className="ob__chips" style={{ marginTop: "0.6rem" }}>
            <button
              className={`ob__chip${allowDoubles ? " ob__chip--active" : ""}`}
              onClick={() => setAllowDoubles(v => !v)}
            >
              Tillåt dubbelpass
            </button>
            <button
              className={`ob__chip${doubleThreshold ? " ob__chip--active" : ""}`}
              onClick={() => setDoubleThreshold(v => !v)}
            >
              Dubbeltröskel (norsk modell)
            </button>
          </div>
          <div className="ob__section-hint">
            Dubbelpass lägger ett andra lätt pass på vissa dagar för högre volym.
            Dubbeltröskel kör två kontrollerade tröskelpass samma dag (norsk modell)
            i bygg-/toppfas — endast för vältränad (kräver god aerob bas).
          </div>
        </section>

        {/* Planning mode */}
        <section className="ob__section">
          <div className="ob__section-label">Planeringsmodell</div>
          <div className="ob__chips">
            {(["tss", "duration"] as const).map(m => (
              <button
                key={m}
                className={`ob__chip${planningMode === m ? " ob__chip--active" : ""}`}
                onClick={() => setPlanningMode(m)}
              >
                {m === "tss" ? "TSS (intensitetsmål)" : "Tid (timmar)"}
              </button>
            ))}
          </div>
          <div className="ob__section-hint">
            {planningMode === "tss"
              ? "Planerar med träningsstresspoäng — kräver puls- eller effektdata per pass."
              : "Planerar enbart med total träningstid per vecka — passar om du inte mäter intensitet."}
          </div>
        </section>

        {/* Training days */}
        <section className="ob__section">
          <div className="ob__section-label">Tillgängliga dagar</div>
          {(["long", "hard", "easy"] as const).map(type => {
            const LABELS = { long: "Långpass", hard: "Intervaller", easy: "Lätta pass" };
            const selected = preferredDays[type];
            // Long days block everything; hard/easy only block long days
            const blockSources = type === "long"
              ? (["hard", "easy", "gym"] as const)
              : (["long"] as const);
            const otherTaken = new Set<number>(
              blockSources.flatMap(t => preferredDays[t])
            );
            return (
              <div key={type} className="ob__day-row">
                <div className="ob__day-row-label">{LABELS[type]}</div>
                <div className="ob__day-chips">
                  {DAY_LABELS.map((lbl, day) => {
                    const isSelected = selected.includes(day);
                    const isTaken = !isSelected && otherTaken.has(day);
                    return (
                      <button
                        key={day}
                        className={`ob__day-chip${isSelected ? " ob__day-chip--active" : ""}${isTaken ? " ob__day-chip--taken" : ""}`}
                        onClick={() => !isTaken && toggleDay(type, day)}
                        disabled={isTaken}
                      >
                        {lbl}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Gym days — only shown when gym is a selected sport */}
          {selectedSports.has("WeightTraining") && (
            <div className="ob__day-row">
              <div className="ob__day-row-label">Gym</div>
              <div className="ob__day-chips">
                {DAY_LABELS.map((lbl, day) => {
                  const isSelected = preferredDays.gym.includes(day);
                  const isTaken = !isSelected && preferredDays.long.includes(day);
                  return (
                    <button
                      key={day}
                      className={`ob__day-chip${isSelected ? " ob__day-chip--active" : ""}${isTaken ? " ob__day-chip--taken" : ""}`}
                      onClick={() => !isTaken && toggleDay("gym", day)}
                      disabled={isTaken}
                    >
                      {lbl}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        {/* Commute */}
        <section className="ob__section">
          <div className="ob__section-label">Pendling (valfritt)</div>
          <div className="ob__commute-toggle">
            <button
              className={`ob__chip${commute ? " ob__chip--active" : ""}`}
              onClick={() => setCommute(c => c ? null : { sport: "Run", minutesOneWay: 20, days: [0,1,2,3,4] })}
            >
              {commute ? "✓ Jag pendlar aktivt" : "Jag pendlar aktivt"}
            </button>
          </div>
          {commute && (
            <div className="ob__commute-config">
              <div className="ob__day-row">
                <div className="ob__day-row-label">Sport</div>
                <div className="ob__day-chips">
                  {(["Run", "Ride"] as const).map(s => (
                    <button
                      key={s}
                      className={`ob__day-chip${commute.sport === s ? " ob__day-chip--active" : ""}`}
                      onClick={() => setCommute(c => c ? { ...c, sport: s } : c)}
                    >
                      {s === "Run" ? "Löpning" : "Cykling"}
                    </button>
                  ))}
                </div>
              </div>
              <div className="ob__day-row">
                <div className="ob__day-row-label">Enkel väg</div>
                <div className="ob__hours-control">
                  <input
                    className="ob__hours-input"
                    type="number" min={5} max={60} step={5}
                    value={commute.minutesOneWay}
                    onChange={e => setCommute(c => c ? { ...c, minutesOneWay: parseInt(e.target.value) } : c)}
                  />
                  <span className="ob__hours-value" style={{ fontSize: "1rem" }}>
                    min ({commute.minutesOneWay * 2} min t/r)
                  </span>
                </div>
              </div>
              <div className="ob__day-row">
                <div className="ob__day-row-label">Pendlingsdagar</div>
                <div className="ob__day-chips">
                  {DAY_LABELS.map((lbl, day) => (
                    <button
                      key={day}
                      className={`ob__day-chip${commute.days.includes(day) ? " ob__day-chip--active" : ""}`}
                      onClick={() => setCommute(c => {
                        if (!c) return c;
                        const days = c.days.includes(day) ? c.days.filter(d => d !== day) : [...c.days, day];
                        return { ...c, days };
                      })}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
              <div className="ob__day-row">
                <div className="ob__day-row-label">Pendlingsmånader</div>
                <div className="ob__chips">
                  <label className="ob__month-field">
                    från
                    <select
                      value={commute.startMonth ?? 1}
                      onChange={e => setCommute(c => c ? { ...c, startMonth: parseInt(e.target.value) } : c)}
                    >
                      {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                    </select>
                  </label>
                  <label className="ob__month-field">
                    till
                    <select
                      value={commute.endMonth ?? 12}
                      onChange={e => setCommute(c => c ? { ...c, endMonth: parseInt(e.target.value) } : c)}
                    >
                      {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                    </select>
                  </label>
                </div>
                <div className="ob__section-hint">
                  Pendlingspass läggs bara in under dessa månader (t.ex. inte mörka vintern).
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Fixed sessions (non-negotiables) */}
        <section className="ob__section">
          <div className="ob__section-label">Fasta pass (icke förhandlingsbara)</div>
          {fixedSessions.map(fsItem => (
            <div key={fsItem.id} className="ob__fixed-row">
              <input
                className="ob__fixed-label"
                placeholder="Namn (t.ex. Lunchlöpning)"
                value={fsItem.label ?? ""}
                onChange={e => updateFixed(fsItem.id, { label: e.target.value })}
              />
              <div className="ob__fixed-controls">
                <select value={fsItem.day} onChange={e => updateFixed(fsItem.id, { day: parseInt(e.target.value) })}>
                  {DAY_LABELS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select>
                <select value={fsItem.sport} onChange={e => updateFixed(fsItem.id, { sport: e.target.value as SportType })}>
                  {ALL_SPORTS.map(s => <option key={s} value={s}>{SPORT_LABELS[s]}</option>)}
                </select>
                <select value={fsItem.kind} onChange={e => updateFixed(fsItem.id, { kind: e.target.value as FixedSession["kind"] })}>
                  <option value="easy">Lätt</option>
                  <option value="quality">Kvalitet</option>
                  <option value="gym">Gym</option>
                </select>
                <label className="ob__month-field">
                  <input type="number" min={15} max={300} step={5} value={fsItem.durationMin}
                    onChange={e => updateFixed(fsItem.id, { durationMin: parseInt(e.target.value) })}
                    style={{ width: 56 }} />
                  min
                </label>
                <button
                  className={`ob__chip${fsItem.biweekly ? " ob__chip--active" : ""}`}
                  onClick={() => updateFixed(fsItem.id, { biweekly: !fsItem.biweekly })}
                >
                  Varannan v.
                </button>
                <button className="ob__fixed-remove" onClick={() => removeFixed(fsItem.id)}>✕</button>
              </div>
            </div>
          ))}
          <button className="ob__manual-toggle" onClick={addFixed}>+ Lägg till fast pass</button>
          <div className="ob__section-hint">
            Pass som alltid ligger fast (t.ex. lunchlöpning med polarna, gym, spinning
            varannan vecka). Planen byggs runt dem och räknar in dem i veckans volym.
          </div>
        </section>

        {/* Blocked / vacation ranges */}
        <section className="ob__section">
          <div className="ob__section-label">Ledighet / blockerade dagar</div>
          {blockedRanges.map(r => (
            <div key={r.id} className="ob__fixed-row">
              <input
                className="ob__fixed-label"
                placeholder="Namn (t.ex. Semester)"
                value={r.label ?? ""}
                onChange={e => updateBlocked(r.id, { label: e.target.value })}
              />
              <div className="ob__fixed-controls">
                <label className="ob__month-field">
                  från
                  <input type="date" value={r.start} onChange={e => updateBlocked(r.id, { start: e.target.value })} />
                </label>
                <label className="ob__month-field">
                  till
                  <input type="date" value={r.end} onChange={e => updateBlocked(r.id, { end: e.target.value })} />
                </label>
                <button className="ob__fixed-remove" onClick={() => removeBlocked(r.id)}>✕</button>
              </div>
            </div>
          ))}
          <button className="ob__manual-toggle" onClick={addBlocked}>+ Lägg till ledighet</button>
          <div className="ob__section-hint">
            Inga pass läggs på dessa dagar och veckans volym minskas. Bra för
            semester, tjänsteresor eller annan frånvaro.
          </div>
        </section>

        {/* Goals */}
        <section className="ob__section">
          <div className="ob__section-label">Mål (valfritt)</div>
          <textarea
            className="ob__textarea"
            placeholder="T.ex. klara Lidingöloppet, bygga bas inför skisäsongen…"
            value={goals}
            onChange={e => setGoals(e.target.value)}
            rows={2}
          />
        </section>

        <button
          className="ob__btn ob__btn--primary"
          disabled={!canSave}
          onClick={handleSave}
        >
          Spara och fortsätt
        </button>
      </div>
    </div>
  );
}
